import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Save, Plus, X, CheckCircle2, ExternalLink } from "lucide-react";
import { useSettings, DEFAULT_SETTINGS } from "@/context/SettingsContext";
import { API, deepClone, inputCls, Field } from "./shared";

function withUids(settings) {
  const d = deepClone(settings);
  (d.services || []).forEach((svc) =>
    (svc.items || []).forEach((it) => {
      if (!it._uid) it._uid = crypto.randomUUID();
    })
  );
  return d;
}

function stripUids(draft) {
  const d = deepClone(draft);
  (d.services || []).forEach((svc) =>
    (svc.items || []).forEach((it) => delete it._uid)
  );
  return d;
}

function ContactSection({ draft, setField }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <h2 className="font-display text-2xl font-bold text-leaf-900">Contact info</h2>
      <p className="text-sm text-leaf-900/60">Shown in nav, footer, and contact page.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <Field label="Phone">
          <input data-testid="settings-phone" className={inputCls} value={draft.phone || ""} onChange={(e) => setField("phone", e.target.value)} />
        </Field>
        <Field label="Email">
          <input data-testid="settings-email" className={inputCls} value={draft.email || ""} onChange={(e) => setField("email", e.target.value)} />
        </Field>
        <Field label="Address (full)">
          <input data-testid="settings-address" className={inputCls} value={draft.address || ""} onChange={(e) => setField("address", e.target.value)} />
        </Field>
        <Field label="Short address (footer)">
          <input data-testid="settings-short-address" className={inputCls} value={draft.short_address || ""} onChange={(e) => setField("short_address", e.target.value)} />
        </Field>
        <Field label="Google Maps embed URL" full>
          <input data-testid="settings-maps" className={inputCls} value={draft.maps_embed || ""} onChange={(e) => setField("maps_embed", e.target.value)} />
          <p className="mt-1 text-xs text-leaf-900/60">
            Tip: in Google Maps → Share → Embed a map → copy the <code>src</code> URL.{" "}
            <a className="underline" target="_blank" rel="noreferrer" href="https://www.google.com/maps">Open Google Maps <ExternalLink className="ml-0.5 inline h-3 w-3" /></a>
          </p>
        </Field>
      </div>
    </section>
  );
}

function HoursSection({ draft, setHour }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <h2 className="font-display text-2xl font-bold text-leaf-900">Hours</h2>
      <p className="text-sm text-leaf-900/60">Use any format you like (e.g. <code>10:00 AM</code> or <code>10–6</code>). Add a <strong>note</strong> like &ldquo;By Appointment&rdquo; to override the time range for that day.</p>
      <div className="mt-5 space-y-2">
        {(draft.hours || []).map((h, i) => (
          <div key={h.day} className="flex flex-wrap items-center gap-3 rounded-xl bg-leaf-50/40 p-3">
            <span className="w-12 font-bold uppercase tracking-wide text-leaf-700">{h.day}</span>
            <label className="flex items-center gap-2 text-sm">
              <input
                data-testid={`settings-hours-${h.day}-closed`}
                type="checkbox"
                checked={!!h.closed}
                onChange={(e) => setHour(i, "closed", e.target.checked)}
              />
              Closed
            </label>
            <input
              data-testid={`settings-hours-${h.day}-open`}
              disabled={h.closed}
              placeholder="Open"
              className={inputCls + " max-w-[130px]"}
              value={h.open || ""}
              onChange={(e) => setHour(i, "open", e.target.value)}
            />
            <span className="text-leaf-900/50">–</span>
            <input
              data-testid={`settings-hours-${h.day}-close`}
              disabled={h.closed}
              placeholder="Close"
              className={inputCls + " max-w-[130px]"}
              value={h.close || ""}
              onChange={(e) => setHour(i, "close", e.target.value)}
            />
            <input
              data-testid={`settings-hours-${h.day}-note`}
              disabled={h.closed}
              placeholder="Note (e.g. By Appointment)"
              className={inputCls + " max-w-[220px]"}
              value={h.note || ""}
              onChange={(e) => setHour(i, "note", e.target.value)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesSection({ draft, setServiceField, setItemField, addItem, removeItem }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <h2 className="font-display text-2xl font-bold text-leaf-900">Services & pricing</h2>
      <p className="text-sm text-leaf-900/60">Edit the service categories shown across the site.</p>
      <div className="mt-5 space-y-6">
        {(draft.services || []).map((s, sIdx) => (
          <div key={s.key} className="rounded-2xl bg-leaf-50/50 p-5">
            <div className="flex flex-wrap items-center gap-3">
              <input
                data-testid={`settings-service-${s.key}-category`}
                className={inputCls + " max-w-md font-semibold"}
                value={s.category || ""}
                onChange={(e) => setServiceField(sIdx, "category", e.target.value)}
              />
            </div>
            <div className="mt-4 space-y-3">
              {(s.items || []).map((it, iIdx) => (
                <div key={it._uid || `${s.key}-${iIdx}`} className="grid gap-2 rounded-xl bg-white p-3 sm:grid-cols-[1.4fr_0.7fr_2fr_auto] sm:items-start">
                  <input
                    data-testid={`settings-service-${s.key}-name-${iIdx}`}
                    className={inputCls}
                    placeholder="Service name"
                    value={it.name || ""}
                    onChange={(e) => setItemField(sIdx, iIdx, "name", e.target.value)}
                  />
                  <input
                    data-testid={`settings-service-${s.key}-price-${iIdx}`}
                    className={inputCls}
                    placeholder="Price"
                    value={it.price || ""}
                    onChange={(e) => setItemField(sIdx, iIdx, "price", e.target.value)}
                  />
                  <input
                    data-testid={`settings-service-${s.key}-note-${iIdx}`}
                    className={inputCls}
                    placeholder="Description / note"
                    value={it.note || ""}
                    onChange={(e) => setItemField(sIdx, iIdx, "note", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(sIdx, iIdx)}
                    aria-label="Remove item"
                    className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addItem(sIdx)}
                className="inline-flex items-center gap-1.5 rounded-full bg-leaf-600 px-4 py-2 text-sm font-bold text-white hover:bg-leaf-700"
              >
                <Plus className="h-4 w-4" /> Add item
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function SettingsTab() {
  const { settings, refresh } = useSettings();
  const [draft, setDraft] = useState(() => withUids(settings));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setDraft(withUids(settings));
  }, [settings]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setHour = (i, key, v) =>
    setDraft((d) => {
      const hours = [...(d.hours || [])];
      hours[i] = { ...hours[i], [key]: v };
      return { ...d, hours };
    });
  const setServiceField = (idx, key, v) =>
    setDraft((d) => {
      const services = [...(d.services || [])];
      services[idx] = { ...services[idx], [key]: v };
      return { ...d, services };
    });
  const setItemField = (sIdx, iIdx, key, v) =>
    setDraft((d) => {
      const services = deepClone(d.services || []);
      services[sIdx].items[iIdx][key] = v;
      return { ...d, services };
    });
  const addItem = (sIdx) =>
    setDraft((d) => {
      const services = deepClone(d.services || []);
      services[sIdx].items.push({ name: "New item", price: "$0", note: "", _uid: crypto.randomUUID() });
      return { ...d, services };
    });
  const removeItem = (sIdx, iIdx) =>
    setDraft((d) => {
      const services = deepClone(d.services || []);
      services[sIdx].items.splice(iIdx, 1);
      return { ...d, services };
    });

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await axios.put(`${API}/settings`, stripUids(draft), { withCredentials: true });
      setSaved(true);
      await refresh();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("Failed to save settings", e);
      setErr(e?.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!window.confirm("Reset all site content to defaults? Your custom values will be lost.")) return;
    setDraft(withUids(DEFAULT_SETTINGS));
  };

  return (
    <div data-testid="admin-settings" className="space-y-8">
      <ContactSection draft={draft} setField={setField} />
      <HoursSection draft={draft} setHour={setHour} />
      <ServicesSection
        draft={draft}
        setServiceField={setServiceField}
        setItemField={setItemField}
        addItem={addItem}
        removeItem={removeItem}
      />

      {/* Save bar */}
      <div className="sticky bottom-20 md:bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-leaf-200 bg-white/95 p-4 shadow-lg backdrop-blur">
        <div className="text-sm text-leaf-900/70">
          {err && <span className="text-clay-500">{err}</span>}
          {!err && saved && (
            <span className="inline-flex items-center gap-1.5 text-leaf-700">
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          )}
          {!err && !saved && <span>Changes apply to all visitors immediately after save.</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={resetDefaults}
            className="rounded-full border-2 border-leaf-200 px-4 py-2 text-sm font-bold text-leaf-900 hover:bg-leaf-50"
          >
            Reset to defaults
          </button>
          <button
            data-testid="settings-save"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </div>
    </div>
  );
}
